import { cn } from "../../lib/utils";

type PageSkeletonProps = {
  /** Home-style hero + cards vs compact content page */
  variant?: "home" | "page";
  className?: string;
};

/**
 * Layout-stable placeholder while lazy routes load.
 * Header lives in AppLayout — this is main content only.
 */
export function PageSkeleton({
  variant = "page",
  className,
}: PageSkeletonProps): JSX.Element {
  if (variant === "home") {
    return (
      <div
        className={cn(
          "w-full min-h-[70vh] bg-gradient-to-br from-blue-50 to-white dark:from-slate-900 dark:to-gray-950",
          className,
        )}
        aria-busy="true"
        aria-label="Loading page"
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 py-12 md:py-20 flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="w-full lg:max-w-[600px] space-y-4 order-2 lg:order-1">
            <div className="h-8 w-40 rounded-full bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
            <div className="h-10 md:h-12 w-[92%] rounded-lg bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
            <div className="h-10 md:h-12 w-[70%] rounded-lg bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
            <div className="h-5 w-full max-w-md rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
            <div className="h-5 w-4/5 max-w-sm rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <div className="h-12 w-full sm:w-40 rounded-lg bg-blue-200/20 dark:bg-blue-900/20 animate-pulse" />
              <div className="h-12 w-full sm:w-36 rounded-lg bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 max-w-md">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-8 w-16 rounded bg-blue-100/20 dark:bg-slate-700/20 animate-pulse" />
                  <div className="h-4 w-24 rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
          <div className="w-full max-w-[320px] sm:max-w-[400px] lg:max-w-[500px] order-1 lg:order-2">
            <div
              className="w-full aspect-[470/802] max-h-[420px] rounded-2xl bg-slate-200/20 dark:bg-slate-700/20 animate-pulse"
              aria-hidden
            />
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 pb-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden border border-slate-200/20 dark:border-slate-800/20 bg-white/10 dark:bg-gray-900/10"
            >
              <div className="h-44 sm:h-48 bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
              <div className="p-5 space-y-3">
                <div className="h-5 w-3/4 rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
                <div className="h-4 w-full rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
                <div className="h-4 w-5/6 rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "w-full min-h-[60vh] bg-gradient-to-br from-white to-blue-50 dark:from-gray-950 dark:to-slate-900",
        className,
      )}
      aria-busy="true"
      aria-label="Loading page"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-16 md:py-20 space-y-8">
        <div className="max-w-2xl mx-auto space-y-4 text-center">
          <div className="h-10 md:h-12 w-2/3 mx-auto rounded-lg bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
          <div className="h-5 w-full rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
          <div className="h-5 w-4/5 mx-auto rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-slate-200/20 dark:border-slate-800/20 p-6 space-y-3 bg-white/10 dark:bg-gray-900/10"
            >
              <div className="h-40 rounded-lg bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
              <div className="h-5 w-3/4 rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
              <div className="h-4 w-full rounded bg-slate-200/20 dark:bg-slate-700/20 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
